import { scoreCommandMatch } from '@/components/layout/command-palette-ranking';
import { agentMatchesSearch, searchTerms } from '@/lib/agent-search';
import type { Agent } from '@/lib/types';

/** Which set of sessions the jump palette is offering. */
export type SessionJumpMode = 'all' | 'recent';

/** A ranked row: the run plus the recency it sorted by. */
export interface SessionJumpEntry {
  agent: Agent;
  /**
   * Epoch-ms this row sorted by — the viewer's last-viewed time when there is
   * one, otherwise the run's own activity time. Rendered as the row's hint.
   */
  recencyAt: number;
  /** True when the viewer has opened this run before (it has a view row). */
  visited: boolean;
}

/**
 * Score a field-level (non-title) hit. Every real fuzzy title score from
 * `command-score` is far above this, so a run whose *name* matches always
 * outranks one that only matches on its prompt, repo, or id.
 */
const FIELD_MATCH_SCORE = 1e-6;

/** The run's own activity timestamp, matching `agentSortTimestamp('activity')`. */
function agentActiveAt(agent: Agent): number {
  return agent.lastActiveAt ?? agent.createdAt;
}

/**
 * How recently the viewer touched this run.
 *
 * A view row whose timestamp is at or below the epoch is the "marked unread"
 * sentinel: it records that the run *was* opened but deliberately rewinds the
 * timestamp, so it cannot be used as a recency. Fall back to the run's own
 * activity time there rather than banishing a hand-flagged run to the bottom
 * of the recents list.
 */
export function sessionRecencyAt(agent: Agent, viewedAt: number | undefined): number {
  return viewedAt !== undefined && viewedAt > 0 ? viewedAt : agentActiveAt(agent);
}

/**
 * Match a run against the jump query, IntelliJ-style: a fuzzy subsequence
 * match on the title first (so `agsi` finds "Agent sidebar search"), falling
 * back to the sidebar's substring semantics over every searchable field
 * (prompt, id, repo, branch, model, linked PR) so pasting an id or recalling a
 * phrase from the prompt still lands. Returns 0 for no match; a blank query
 * matches everything.
 */
export function scoreSessionMatch(agent: Agent, query: string): number {
  return sessionMatcher(query)(agent);
}

/**
 * `scoreSessionMatch` bound to one query, so the field-level terms are parsed
 * once per keystroke rather than once per run in the list.
 */
function sessionMatcher(query: string): (agent: Agent) => number {
  const q = query.trim();
  if (!q) return () => 1;
  const terms = searchTerms(q);
  return (agent) => {
    const titleScore = scoreCommandMatch(agent.name, q);
    if (titleScore > 0) return titleScore;
    return agentMatchesSearch(agent, terms) ? FIELD_MATCH_SCORE : 0;
  };
}

export interface RankSessionJumpOptions {
  agents: readonly Agent[];
  /** The viewer's `agentId -> lastViewedAt` map from `useAgentViews`. */
  views: Readonly<Record<string, number>>;
  query: string;
  mode: SessionJumpMode;
  limit: number;
}

/**
 * Rank runs for the jump palette.
 *
 * `all` sorts by match quality — the point of the surface is to type a few
 * letters and hit Enter, so the best title match has to be the selected row —
 * and breaks ties by recency, which makes the empty query a most-recent list.
 *
 * `recent` keeps strict recency order and only narrows as you type: it answers
 * "the thing I was just in", where re-ranking by match quality would move rows
 * out from under a hand that already knows where they are. It is restricted to
 * runs the viewer has actually opened, so it is a history, not a second copy
 * of the full list.
 */
export function rankSessionJumpEntries({
  agents,
  views,
  query,
  mode,
  limit,
}: RankSessionJumpOptions): SessionJumpEntry[] {
  const scoreAgent = sessionMatcher(query);
  const scored: { entry: SessionJumpEntry; score: number }[] = [];
  for (const agent of agents) {
    const viewedAt = views[agent.id];
    const visited = viewedAt !== undefined;
    if (mode === 'recent' && !visited) continue;
    const score = scoreAgent(agent);
    if (score === 0) continue;
    scored.push({
      entry: { agent, recencyAt: sessionRecencyAt(agent, viewedAt), visited },
      score,
    });
  }

  scored.sort((a, b) => {
    if (mode === 'all' && b.score !== a.score) return b.score - a.score;
    if (b.entry.recencyAt !== a.entry.recencyAt) return b.entry.recencyAt - a.entry.recencyAt;
    return a.entry.agent.name.localeCompare(b.entry.agent.name);
  });

  return scored.slice(0, limit).map(({ entry }) => entry);
}
